import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import type { SearchCriteria } from '../../../../../js/@types/SearchCriteria';
import type { ConnectionTypeCategory } from '../../../../../js/@types/SearchFilters';

export interface ConnectionFilterProps {
    allConnectionTypes: string[];
    connectionTypeCategories: ConnectionTypeCategory[];
    unavailableConnectionTypes?: Array<{ shortUrl: string; id?: number }>;
    connectionTypesToIgnore?: string[];
    allConnectionsSelected?: boolean | null;
    current: SearchCriteria['connectionTypes'];
    onChanged: (
        value: string | string[] | { connectionTypes: string | string[]; allConnectionTypes?: string[] }
    ) => void;
    tabToConnectionTypesMapping?: { [tabName: string]: string[] };
    tab?: string;
}

const SELECT_ALL_VALUE = '__select_all__';
const CONNECTION_SELECT_ID = 'connectionTypesSelect';

const ConnectionFilter = ({
    current,
    onChanged,
    connectionTypeCategories,
    allConnectionTypes,
    allConnectionsSelected,
    unavailableConnectionTypes
}: ConnectionFilterProps) => {
    const safeCategories = useMemo(() => connectionTypeCategories ?? [], [connectionTypeCategories]);

    const allConnectionTypesShortUrls = useMemo(() => {
        return safeCategories.flatMap(category => category.connectionTypes.map(type => type.shortUrl));
    }, [safeCategories]);

    const unavailableShortUrls = useMemo(
        () => unavailableConnectionTypes?.map(ct => ct.shortUrl) ?? [],
        [unavailableConnectionTypes]
    );
    const unavailableShortUrlSet = useMemo(() => new Set(unavailableShortUrls), [unavailableShortUrls]);

    const baseAvailableTypes = useMemo(
        () => allConnectionTypes ?? allConnectionTypesShortUrls,
        [allConnectionTypes, allConnectionTypesShortUrls]
    );

    const allAvailableConnectionTypes = useMemo(() => {
        return baseAvailableTypes.filter(type => !unavailableShortUrlSet.has(type));
    }, [baseAvailableTypes, unavailableShortUrlSet]);

    const connectionTypeOptionGroups = useMemo(() => {
        return safeCategories.map(category => (
            <optgroup key={category.title} label={category.title}>
                {category.connectionTypes.map(connectionType => (
                    <option
                        key={connectionType.shortUrl}
                        value={connectionType.shortUrl}
                        disabled={unavailableShortUrlSet.has(connectionType.shortUrl)}
                        dangerouslySetInnerHTML={{ __html: connectionType.title }}
                    />
                ))}
            </optgroup>
        ));
    }, [safeCategories, unavailableShortUrlSet]);

    // Track if we need to skip external sync (when clearing)
    const skipExternalSyncRef = useRef(false);

    const currentValues = useMemo(() => current?.values ?? [], [current?.values]);

    const isAllEnabledSelected = useMemo(() => {
        return (
            allConnectionsSelected === true ||
            (currentValues.length === allAvailableConnectionTypes.length &&
                allAvailableConnectionTypes.every(type => currentValues.includes(type)))
        );
    }, [currentValues, allAvailableConnectionTypes, allConnectionsSelected]);

    const syncPickerToValues = useCallback((valuesToSet: string[]) => {
        const selectElement = document.getElementById(CONNECTION_SELECT_ID) as HTMLSelectElement | null;
        if (!selectElement || typeof wo$ === 'undefined') return;

        const $select = wo$(selectElement);

        if (!$select.data('selectpicker')) {
            if (typeof WhistleOut !== 'undefined') {
                WhistleOut.applySelectPicker($select, {
                    countSelectedText: (numSelected: number) => {
                        const el = document.getElementById(CONNECTION_SELECT_ID) as HTMLSelectElement | null;
                        const hasSelectAll =
                            el &&
                            Array.from(el.selectedOptions).some((o: HTMLOptionElement) => o.value === SELECT_ALL_VALUE);
                        const displayCount = hasSelectAll ? Math.max(0, numSelected - 1) : numSelected;
                        if (displayCount === 0) return 'Connection Types';
                        return displayCount === 1 ? '1 item selected' : `${displayCount} items selected`;
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any);
            }
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ($select.selectpicker as any)('val', valuesToSet);
        $select.selectpicker('refresh');
    }, []);

    const getPickerValues = useCallback((selectElement: HTMLSelectElement): string[] => {
        if (typeof wo$ === 'undefined') return [];
        const $select = wo$(selectElement);
        if (!$select.data('selectpicker')) return [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pickerVal = ($select.selectpicker as any)('val');
        return Array.isArray(pickerVal) ? pickerVal : pickerVal ? [pickerVal] : [];
    }, []);

    const getSelectedValues = useCallback(
        (selectElement: HTMLSelectElement): string[] => {
            let selectedValues = Array.from(selectElement.selectedOptions)
                .map(option => option.value)
                .filter(value => value !== SELECT_ALL_VALUE && !unavailableShortUrlSet.has(value));

            if (selectedValues.length === 0) {
                const pickerValues = getPickerValues(selectElement);
                selectedValues = pickerValues.filter(
                    value => value !== SELECT_ALL_VALUE && !unavailableShortUrlSet.has(value)
                );
            }
            return selectedValues;
        },
        [unavailableShortUrlSet, getPickerValues]
    );

    const syncOptionsToValues = useCallback((selectElement: HTMLSelectElement, values: string[]): void => {
        Array.from(selectElement.options).forEach(opt => {
            if (opt.value !== SELECT_ALL_VALUE) {
                opt.selected = values.includes(opt.value);
            }
        });
    }, []);

    const selectAllAvailable = useCallback(
        (selectElement: HTMLSelectElement): void => {
            Array.from(selectElement.options).forEach(opt => {
                if (opt.value === SELECT_ALL_VALUE || !unavailableShortUrlSet.has(opt.value)) {
                    opt.selected = true;
                }
            });
        },
        [unavailableShortUrlSet]
    );

    const handleSelectAllToggled = useCallback(
        (selectElement: HTMLSelectElement, selectedValues: string[], wasSelectAllBefore: boolean): void => {
            const selectAllOption = Array.from(selectElement.options).find(opt => opt.value === SELECT_ALL_VALUE) as
                | HTMLOptionElement
                | undefined;
            const isSelectAllNow = selectAllOption?.selected ?? false;

            if (isSelectAllNow && !wasSelectAllBefore) {
                selectAllAvailable(selectElement);
                skipExternalSyncRef.current = false;
                onChanged({ connectionTypes: allAvailableConnectionTypes, allConnectionTypes: baseAvailableTypes });
                syncPickerToValues([SELECT_ALL_VALUE, ...allAvailableConnectionTypes]);
            } else if (
                isSelectAllNow &&
                wasSelectAllBefore &&
                selectedValues.length < allAvailableConnectionTypes.length
            ) {
                if (selectAllOption) selectAllOption.selected = false;
                skipExternalSyncRef.current = false;
                onChanged(selectedValues.length === 0 ? [] : selectedValues);
                syncPickerToValues(selectedValues);
            } else if (
                isSelectAllNow &&
                wasSelectAllBefore &&
                selectedValues.length === allAvailableConnectionTypes.length
            ) {
                if (selectAllOption) selectAllOption.selected = false;
                Array.from(selectElement.options).forEach(opt => {
                    if (opt.value !== SELECT_ALL_VALUE) opt.selected = false;
                });
                skipExternalSyncRef.current = true;
                onChanged([]);
                syncPickerToValues([]);
            } else if (!isSelectAllNow && wasSelectAllBefore) {
                Array.from(selectElement.options).forEach(opt => {
                    opt.selected = false;
                });
                skipExternalSyncRef.current = true;
                onChanged([]);
                syncPickerToValues([]);
            }
        },
        [onChanged, allAvailableConnectionTypes, baseAvailableTypes, selectAllAvailable, syncPickerToValues]
    );

    const handleManualSelection = useCallback(
        (selectElement: HTMLSelectElement, selectedValues: string[]): void => {
            if (selectedValues.length === 0) {
                syncOptionsToValues(selectElement, []);
                skipExternalSyncRef.current = false;
                onChanged([]);
                syncPickerToValues([]);
            } else {
                const allManuallySelected =
                    selectedValues.every(v => allAvailableConnectionTypes.includes(v)) &&
                    selectedValues.length === allAvailableConnectionTypes.length;

                if (allManuallySelected) {
                    selectAllAvailable(selectElement);
                    skipExternalSyncRef.current = false;
                    onChanged({ connectionTypes: allAvailableConnectionTypes, allConnectionTypes: baseAvailableTypes });
                    syncPickerToValues([SELECT_ALL_VALUE, ...allAvailableConnectionTypes]);
                } else {
                    syncOptionsToValues(selectElement, selectedValues);
                    skipExternalSyncRef.current = false;
                    onChanged(selectedValues);
                    syncPickerToValues(selectedValues);
                }
            }
        },
        [
            allAvailableConnectionTypes,
            baseAvailableTypes,
            selectAllAvailable,
            syncOptionsToValues,
            syncPickerToValues,
            onChanged
        ]
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const selectElement = e.target as HTMLSelectElement;
            const selectedValues = getSelectedValues(selectElement);
            const selectAllOption = Array.from(selectElement.options).find(opt => opt.value === SELECT_ALL_VALUE) as
                | HTMLOptionElement
                | undefined;
            const wasSelectAllBefore = isAllEnabledSelected;
            const isSelectAllNow = selectAllOption?.selected ?? false;

            syncOptionsToValues(selectElement, selectedValues);

            if (wasSelectAllBefore && !isSelectAllNow && selectedValues.length === allAvailableConnectionTypes.length) {
                Array.from(selectElement.options).forEach(opt => {
                    opt.selected = false;
                });
                skipExternalSyncRef.current = true;
                onChanged([]);
                syncPickerToValues([]);
                return;
            }

            if (isSelectAllNow) {
                handleSelectAllToggled(selectElement, selectedValues, wasSelectAllBefore);
            } else {
                handleManualSelection(selectElement, selectedValues);
            }
        },
        [
            getSelectedValues,
            isAllEnabledSelected,
            allAvailableConnectionTypes,
            syncOptionsToValues,
            handleSelectAllToggled,
            handleManualSelection,
            onChanged,
            syncPickerToValues
        ]
    );

    useEffect(() => {
        const selectElement = document.getElementById(CONNECTION_SELECT_ID) as HTMLSelectElement | null;
        if (!selectElement || typeof wo$ === 'undefined') return;

        const $select = wo$(selectElement);
        const handleHidden = () => {
            skipExternalSyncRef.current = false;
            const selectedValues = Array.from(selectElement.selectedOptions)
                .map(opt => opt.value)
                .filter(v => v !== SELECT_ALL_VALUE && !unavailableShortUrlSet.has(v));

            if (selectedValues.length === 0) {
                skipExternalSyncRef.current = false;
                onChanged({
                    connectionTypes: allAvailableConnectionTypes,
                    allConnectionTypes: baseAvailableTypes
                });
                syncPickerToValues([SELECT_ALL_VALUE, ...allAvailableConnectionTypes]);
            }
        };

        $select.on('hidden.bs.select', handleHidden);

        return () => {
            $select.off('hidden.bs.select', handleHidden);
        };
    }, [onChanged, allAvailableConnectionTypes, baseAvailableTypes, unavailableShortUrlSet, syncPickerToValues]);

    // Initialize selectpicker and sync initial values
    useEffect(() => {
        const selectElement = document.getElementById(CONNECTION_SELECT_ID) as HTMLSelectElement | null;
        if (!selectElement || typeof wo$ === 'undefined' || typeof WhistleOut === 'undefined') return;

        const $select = wo$(selectElement);
        if (!$select.data('selectpicker')) {
            WhistleOut.applySelectPicker($select, {
                countSelectedText: (numSelected: number) => {
                    const el = document.getElementById(CONNECTION_SELECT_ID) as HTMLSelectElement | null;
                    const hasSelectAll = el && Array.from(el.selectedOptions).some(o => o.value === SELECT_ALL_VALUE);
                    const displayCount = hasSelectAll ? Math.max(0, numSelected - 1) : numSelected;
                    if (displayCount === 0) return 'Connection Types';
                    return displayCount === 1 ? '1 item selected' : `${displayCount} items selected`;
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);
        }

        // Retry syncing values until selectpicker is ready
        let retries = 0;
        const maxRetries = 20;
        const syncInterval = setInterval(() => {
            const $selectCheck = wo$(selectElement);
            if ($selectCheck.data('selectpicker')) {
                const pickerValues = isAllEnabledSelected
                    ? [SELECT_ALL_VALUE, ...allAvailableConnectionTypes]
                    : currentValues;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ($selectCheck.selectpicker as any)('val', pickerValues);
                $selectCheck.selectpicker('refresh');
                clearInterval(syncInterval);
            } else if (retries >= maxRetries) {
                clearInterval(syncInterval);
            }
            retries++;
        }, 50);

        return () => clearInterval(syncInterval);
        // Only run on mount - don't watch currentValues/isAllEnabledSelected
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (skipExternalSyncRef.current) return;

        const selectElement = document.getElementById(CONNECTION_SELECT_ID) as HTMLSelectElement | null;
        if (!selectElement || typeof wo$ === 'undefined') return;

        const $select = wo$(selectElement);
        if (!$select.data('selectpicker')) return;

        const desiredValues = isAllEnabledSelected
            ? [SELECT_ALL_VALUE, ...allAvailableConnectionTypes]
            : currentValues.filter(v => !unavailableShortUrlSet.has(v));

        const actualValues = Array.from(selectElement.selectedOptions).map(opt => opt.value);
        const isSame =
            desiredValues.length === actualValues.length && desiredValues.every(v => actualValues.includes(v));

        if (!isSame) {
            syncPickerToValues(desiredValues);
        }
    }, [currentValues, isAllEnabledSelected, allAvailableConnectionTypes, unavailableShortUrlSet, syncPickerToValues]);

    return (
        <>
            <div className="mar-b-4 font-4 c-gray-light line-height-11">Connection</div>
            <label className="sr-only" id="connectionTypesSelectLabel" htmlFor="connectionTypesSelect">
                Connection Types
            </label>
            <div className="selectpicker-wide">
                <select
                    id={CONNECTION_SELECT_ID}
                    name="connectionTypes"
                    tabIndex={-1}
                    className="selectpicker bs-select-hidden dropdown bootstrap-select show-tick bs3"
                    multiple
                    aria-label="Connection Types"
                    aria-labelledby="connectionTypesSelectLabel"
                    data-selected-text-format="count>1"
                    title="Connection Types"
                    onChange={handleChange}
                    data-dropup-auto="false"
                    data-style="btn-default rounded-2"
                >
                    <optgroup key={'all'} label={'All Connection Types'}>
                        <option value={SELECT_ALL_VALUE} key={SELECT_ALL_VALUE}>
                            Select All
                        </option>
                    </optgroup>
                    {connectionTypeOptionGroups}
                </select>
            </div>
        </>
    );
};

export default memo(ConnectionFilter);
